// ─────────────────────────────────────────────────────────────────────────────
// Crew — app frame (Agent B): header + mobile-first bottom nav.
//
// The nav only appears once the user is in a group (the funnel surfaces). Tap
// targets are ≥44px; the active tab is announced via aria-current.
// ─────────────────────────────────────────────────────────────────────────────

import type { ReactNode } from 'react';
import type { Route } from '../lib/router';
import { navigate } from '../lib/router';

interface AppShellProps {
  route: Route;
  showNav: boolean;
  email: string | null;
  onSignOut: () => void;
  children: ReactNode;
}

const NAV: { route: Route; label: string }[] = [
  { route: 'group', label: 'Group' },
  { route: 'activities', label: 'Activities' },
  { route: 'settings', label: 'Settings' },
];

export function AppShell({ route, showNav, email, onSignOut, children }: AppShellProps) {
  return (
    <div className="flex min-h-full flex-col">
      <header className="sticky top-0 z-10 border-b border-sand-200 bg-sand-50/90 backdrop-blur">
        <div className="mx-auto flex w-full max-w-2xl items-center justify-between px-4 py-3">
          <button
            type="button"
            onClick={() => navigate(showNav ? 'group' : 'home')}
            className="text-xl font-semibold tracking-tight text-ink-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-coral-500 rounded"
          >
            Crew
          </button>
          {email && (
            <div className="flex items-center gap-3 text-sm">
              <span className="hidden text-ink-700/70 sm:inline" title={email}>
                {email}
              </span>
              <button
                type="button"
                onClick={onSignOut}
                className="min-h-[44px] rounded-xl2 px-3 font-medium text-ink-700 hover:bg-sand-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-coral-500"
              >
                Sign out
              </button>
            </div>
          )}
        </div>
      </header>

      <main className="mx-auto w-full max-w-2xl flex-1 px-4 py-6 pb-28">{children}</main>

      {showNav && (
        <nav
          aria-label="Primary"
          className="fixed inset-x-0 bottom-0 z-10 border-t border-sand-200 bg-sand-50/95 backdrop-blur"
        >
          <div className="mx-auto flex w-full max-w-2xl">
            {NAV.map((item) => {
              const active = route === item.route;
              return (
                <button
                  key={item.route}
                  type="button"
                  aria-current={active ? 'page' : undefined}
                  onClick={() => navigate(item.route)}
                  className={
                    'flex min-h-[56px] flex-1 flex-col items-center justify-center gap-0.5 text-xs font-semibold transition ' +
                    (active ? 'text-coral-600' : 'text-ink-700/60 hover:text-ink-800')
                  }
                >
                  <span
                    aria-hidden
                    className={'h-1.5 w-1.5 rounded-full ' + (active ? 'bg-coral-500' : 'bg-transparent')}
                  />
                  {item.label}
                </button>
              );
            })}
          </div>
        </nav>
      )}
    </div>
  );
}
